import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  makeCamera,
  altAzToVector,
  projectVector,
  starColor,
  extinctionFactor,
  daylightFactor,
  moonLitPath,
  brightLimbAngle,
  chooseStarLabels,
  CARDINALS,
  bearingName,
  MAX_PITCH_DEG,
  MIN_PITCH_DEG
} from './skyRender.mjs';
import { horizontalVectorToEquatorial, julianDate, lstDegrees } from './astronomy.mjs';
import { horizontalFovDeg } from './viewport.mjs';
import { loreFor } from './constellationLore.mjs';
import BrightnessTuner, { useBrightnessCurve } from './BrightnessTuner.jsx';
import { useSettings } from './SettingsMenu.jsx';
import { makeCurve } from './brightness.mjs';

const MIN_FOV = 25;
const MAX_FOV = 110;

/*
 * How much room a hovercard needs to the right of its star: the widest the card
 * gets, plus the 14px it is offset by. Kept beside `.sg-star-card`'s max-width in
 * the stylesheet, and pinned to it by a test, since a card that has quietly grown
 * wider than this would start being clipped again with nothing to say so.
 */
const CARD_REACH = 264;

/*
 * Rounds open fully zoomed out. Field of view is in degrees of sky across the
 * frame, so the widest setting is the one that shows the most: a narrow opening
 * view drops you into an anonymous patch between the famous shapes. Tied to
 * MAX_FOV so the two cannot drift apart.
 */
const DEFAULT_FOV = MAX_FOV;

// Low enough that the horizon is in frame from the first moment. Judging a
// star's height against it is the whole latitude measurement, so opening the
// round with nothing to measure against would hide the game's main tool.
const START_PITCH_DEG = 24;

// The Milky Way is painted into a buffer this many times smaller than the canvas
// and scaled up. It is a diffuse glow with no edges to lose, and the per-pixel
// unprojection is the one genuinely expensive thing in the frame.
const MILKY_WAY_DOWNSCALE = 4;

const MOON_RADIUS_PX = 26;

// Names per round, chosen across the whole sky above the horizon. A wide view
// shows roughly a quarter of it, so this lands about four names in frame.
const MAX_STAR_LABELS = 16;

// Shapes only: the lesson's own overlay names what matters.
const GUIDE_LAYERS = { stars: false, constellations: true, planets: false };

// Planets read faintly warm next to the stars, which is close enough to how
// Mars and Jupiter actually look and helps tell them apart at a glance.
const PLANET_COLOR = [255, 241, 214];

function planetAsTarget(planet) {
  return {
    name: planet.name,
    bayer: '',
    constellation: '',
    mag: planet.mag,
    distance: 0,
    distanceAu: planet.distanceAu,
    spectralClass: '',
    alt: planet.alt,
    dec: planet.dec,
    isPlanet: true
  };
}

/*
 * Star sprites are pre-rendered once per (colour, size) bucket and stamped with
 * drawImage. Building a radial gradient per star per frame is what makes a naive
 * canvas starfield stutter, and there are a couple of thousand of them.
 */
function makeStarSprite(color, radius, coreFloor) {
  /*
   * The core has to stay at least a pixel across. A gradient that starts falling
   * off immediately puts its bright centre inside a single pixel, and rasterising
   * averages that peak away, so every star comes out grey no matter how bright it
   * is meant to be. Hence a flat, fully bright core, then the halo.
   */
  const core = Math.max(coreFloor, radius);
  const halo = core * 3.2;
  const size = Math.ceil(halo * 2);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  const [r, g, b] = color;
  const coreStop = core / halo;

  const gradient = ctx.createRadialGradient(halo, halo, 0, halo, halo, halo);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(coreStop * 0.7, `rgba(${r},${g},${b},1)`);
  gradient.addColorStop(coreStop, `rgba(${r},${g},${b},0.5)`);
  gradient.addColorStop(coreStop + (1 - coreStop) * 0.35, `rgba(${r},${g},${b},0.12)`);
  gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return { canvas, offset: halo };
}

function spriteFor(cache, color, radius, coreFloor) {
  const bucket = Math.max(1, Math.round(radius * 3));
  // The floor is part of the key: two curves asking for the same radius can want
  // different cores, and a shared sprite would hand one of them the other's.
  const key = `${color[0]}_${bucket}_${coreFloor}`;
  if (!cache.has(key)) cache.set(key, makeStarSprite(color, bucket / 3, coreFloor));
  return cache.get(key);
}

// Extinction is smooth and only ever read through sin(altitude), so a small
// lookup beats calling pow once per Milky Way pixel.
const EXTINCTION_LUT = Array.from({ length: 129 }, (_, i) => extinctionFactor((Math.asin(i / 128) * 180) / Math.PI));

/*
 * A contrast curve for the Milky Way texture. Straight from the file it renders
 * as flat grey haze, because most of its area is a low, fairly uniform value
 * that a linear ramp turns into fog. Pushing the dim end down separates the dust
 * lanes from the glow and lets the bright galactic centre carry the image.
 */
const MILKY_WAY_CURVE = Array.from({ length: 256 }, (_, v) => 255 * (v / 255) ** 2.1);

function drawMilkyWay(target, camera, texture, latDeg, lstDeg, washout, starlight = 1, preset = 1) {
  const width = target.width;
  const height = target.height;
  const image = target.ctx.createImageData(width, height);
  const out = image.data;

  const toEq = (v) => horizontalVectorToEquatorial(v.east, v.north, v.up, latDeg, lstDeg);
  const rEq = toEq(camera.right);
  const uEq = toEq(camera.up);
  const fEq = toEq(camera.forward);

  const scaleX = camera.width / width;
  const scaleY = camera.height / height;
  /*
   * The real Milky Way is a faint wash you need a dark site to notice, and the
   * stars read as points on top of it. Turned up it swamps them and the sky
   * stops looking like a sky.
   *
   * Two things pushed it over that line. The faint stars got dimmer when the
   * brightness curve was retuned, which left the band the brightest thing in
   * frame by comparison; and the band is dimmed through EXTINCTION_LUT, so
   * backing the horizon extinction off from 0.18 to 0.126 lifted it again —
   * only 7% averaged over the sky, but 27% at ten degrees up and 68% at five,
   * which is exactly where the galactic centre sits on a southern summer night.
   *
   * Compensating for the extinction alone would have meant 0.58. This sits below
   * that on purpose — at 0.62 the band was brighter than Antares — but not as
   * far below as the 0.35 it was first cut to, which took it further than it
   * needed to go once `preset` started scaling it as well. Halfway between the
   * two, and the per-preset factor does the rest of the work.
   */
  const gain = 0.485 * preset * (1 - 0.55 * washout) * starlight;

  for (let py = 0; py < height; py += 1) {
    const yc = -((py + 0.5) * scaleY - camera.centerY) / camera.focal;
    const upComponent = yc * camera.up.up + camera.forward.up;

    for (let px = 0; px < width; px += 1) {
      const xc = ((px + 0.5) * scaleX - camera.centerX) / camera.focal;
      const length = Math.sqrt(xc * xc + yc * yc + 1);
      const sinAlt = upComponent / length;
      if (sinAlt <= 0) continue;

      const ex = xc * rEq.x + yc * uEq.x + fEq.x;
      const ey = xc * rEq.y + yc * uEq.y + fEq.y;
      const ez = xc * rEq.z + yc * uEq.z + fEq.z;

      let u = Math.atan2(ey, ex) / (2 * Math.PI);
      if (u < 0) u += 1;
      const v = 0.5 - Math.asin(ez / length) / Math.PI;

      const sx = Math.min(texture.width - 1, (u * texture.width) | 0);
      const sy = Math.min(texture.height - 1, Math.max(0, (v * texture.height) | 0));
      const si = (sy * texture.width + sx) * 4;

      const dim = EXTINCTION_LUT[(sinAlt * 128) | 0] * gain;
      const di = (py * width + px) * 4;
      out[di] = Math.min(255, MILKY_WAY_CURVE[texture.data[si]] * dim);
      out[di + 1] = Math.min(255, MILKY_WAY_CURVE[texture.data[si + 1]] * dim);
      out[di + 2] = Math.min(255, MILKY_WAY_CURVE[texture.data[si + 2]] * dim);
      out[di + 3] = 255;
    }
  }

  target.ctx.putImageData(image, 0, 0);
}

/*
 * The Moon's face, built once and reused.
 *
 * The near side is centred on lunar longitude 0, which is the middle column of
 * the equirectangular map, so each pixel of the disc is unprojected back onto the
 * sphere and sampled: longitude from atan2 across the disc, latitude from asin
 * down it. Doing this per frame would be wasted work, since only the disc's
 * position on screen changes as the camera pans.
 */
function makeMoonFace(texture, radius) {
  const size = Math.ceil(radius * 2);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  const image = ctx.createImageData(size, size);
  const out = image.data;

  for (let py = 0; py < size; py += 1) {
    const ny = (py + 0.5 - radius) / radius;
    for (let px = 0; px < size; px += 1) {
      const nx = (px + 0.5 - radius) / radius;
      const r2 = nx * nx + ny * ny;
      if (r2 >= 1) continue;

      const nz = Math.sqrt(1 - r2);
      const u = 0.5 + Math.atan2(nx, nz) / (2 * Math.PI);
      const v = 0.5 + Math.asin(ny) / Math.PI;

      const sx = Math.min(texture.width - 1, Math.max(0, (u * texture.width) | 0));
      const sy = Math.min(texture.height - 1, Math.max(0, (v * texture.height) | 0));
      const si = (sy * texture.width + sx) * 4;
      const di = (py * size + px) * 4;

      // Limb darkening: the edge of the disc is seen at a grazing angle and is
      // measurably dimmer. It is most of what stops a flat texture reading as a
      // sticker rather than a sphere.
      const shade = 0.55 + 0.45 * nz ** 0.6;
      out[di] = texture.data[si] * shade;
      out[di + 1] = texture.data[si + 1] * shade;
      out[di + 2] = texture.data[si + 2] * shade;
      out[di + 3] = 255;
    }
  }

  ctx.putImageData(image, 0, 0);
  return canvas;
}

function drawMoon(ctx, camera, moon, sun, face) {
  const moonVec = altAzToVector(moon.alt, moon.az);
  const projected = projectVector(moonVec, camera);
  if (!projected) return;

  const { x, y } = projected;
  const dim = extinctionFactor(moon.alt);
  if (dim <= 0) return;

  ctx.save();

  const glowRadius = MOON_RADIUS_PX * 5;
  const glow = ctx.createRadialGradient(x, y, MOON_RADIUS_PX, x, y, glowRadius);
  glow.addColorStop(0, `rgba(200,218,255,${0.20 * moon.illuminated * dim})`);
  glow.addColorStop(0.45, `rgba(200,218,255,${0.05 * moon.illuminated * dim})`);
  glow.addColorStop(1, 'rgba(200,218,255,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.globalAlpha = dim;

  if (face) ctx.drawImage(face, x - MOON_RADIUS_PX, y - MOON_RADIUS_PX);
  else {
    ctx.fillStyle = '#f4f1e4';
    ctx.beginPath();
    ctx.arc(x, y, MOON_RADIUS_PX, 0, Math.PI * 2);
    ctx.fill();
  }

  /*
   * The unlit part is painted over the finished face rather than the lit part
   * being cut out of it: fill the whole disc and the lit outline together under
   * the even-odd rule and what remains is exactly the shadow, at any phase, with
   * the texture still showing through underneath the terminator's edge.
   */
  const limbAngle = brightLimbAngle(moonVec, altAzToVector(sun.alt, sun.az), camera);
  const cos = Math.cos(limbAngle);
  const sin = Math.sin(limbAngle);

  ctx.beginPath();
  ctx.arc(x, y, MOON_RADIUS_PX, 0, Math.PI * 2);
  ctx.moveTo(x + MOON_RADIUS_PX * cos, y + MOON_RADIUS_PX * sin);
  moonLitPath(moon.illuminated, MOON_RADIUS_PX).forEach(([dx, dy], index) => {
    const rx = x + dx * cos - dy * sin;
    const ry = y + dx * sin + dy * cos;
    if (index === 0) ctx.moveTo(rx, ry);
    else ctx.lineTo(rx, ry);
  });
  ctx.closePath();
  ctx.fillStyle = 'rgba(6, 8, 14, 0.94)';
  ctx.fill('evenodd');

  ctx.restore();
}

/*
 * Constellation figures and the names of the brightest stars, drawn only once
 * the guess is locked in. Before that they would simply hand over the answer:
 * "Crux" on screen names the hemisphere, and Polaris labelled is latitude read
 * off a caption instead of the sky.
 */
/*
 * Cardinal points marked along the horizon, so after the fact you can see which
 * way you were facing and check the reasoning: that Polaris really was due north
 * and at your latitude, or which stars were sitting on the meridian.
 */
function drawCompass(ctx, camera, dpr) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.font = `700 ${13 * dpr}px system-ui, sans-serif`;

  for (const [label, azimuth] of CARDINALS) {
    const point = projectVector(altAzToVector(0, azimuth), camera);
    if (!point || point.x < 0 || point.x > camera.width) continue;

    const major = label.length === 1;
    ctx.strokeStyle = major ? 'rgba(180, 210, 255, 0.75)' : 'rgba(150, 180, 225, 0.4)';
    ctx.fillStyle = major ? 'rgba(210, 228, 255, 0.95)' : 'rgba(170, 195, 235, 0.6)';
    ctx.lineWidth = (major ? 2 : 1) * dpr;

    const tick = (major ? 16 : 9) * dpr;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineTo(point.x, point.y - tick);
    ctx.stroke();

    ctx.fillText(label, point.x, point.y - tick - 5 * dpr);
  }

  ctx.restore();
}

function drawAnnotations(ctx, camera, round, dpr, layers, namedStars, labelHits) {
  ctx.save();
  ctx.lineWidth = 1 * dpr;
  ctx.strokeStyle = 'rgba(126, 170, 235, 0.34)';

  if (layers.constellations) {

  for (const figure of round.constellations) {
    for (const line of figure.lines) {
      let started = false;
      ctx.beginPath();
      for (const point of line) {
        const p = projectVector(altAzToVector(point.alt, point.az), camera);
        if (!p) {
          started = false;
          continue;
        }
        if (started) ctx.lineTo(p.x, p.y);
        else ctx.moveTo(p.x, p.y);
        started = true;
      }
      ctx.stroke();
    }
  }

  }

  /*
   * One label to a patch of screen.
   *
   * Star names, planet names and constellation names are three passes over three
   * separate lists, and until now nothing stopped them landing on top of each
   * other: "Antares" printed straight across its own constellation's name.
   * `chooseStarLabels` already spaces star names from each other, but it works
   * in sky coordinates and knows nothing about the other two sets.
   *
   * Boxes are reserved in priority order — planets, then stars brightest-first,
   * then constellations — so the label that loses a collision is always the
   * least specific one. A dropped constellation name loses nothing: its lines
   * are still drawn, and the shape is what names it.
   */
  const placed = [];
  const PAD = 2 * dpr;
  const reserve = (x0, y0, x1, y1) => {
    const box = { x0: x0 - PAD, y0: y0 - PAD, x1: x1 + PAD, y1: y1 + PAD };
    const hit = placed.some(
      (other) => box.x0 < other.x1 && box.x1 > other.x0 && box.y0 < other.y1 && box.y1 > other.y0
    );
    if (hit) return false;
    placed.push(box);
    return true;
  };

  // Drawn from the mark's upper left, which is where both stars and planets put
  // their names, so one box shape covers them.
  const besideMark = (p, text, size) => {
    const x = p.x + 9 * dpr;
    const y = p.y - 6 * dpr;
    return reserve(x, y - size, x + ctx.measureText(text).width, y + size * 0.25)
      ? { x, y }
      : null;
  };

  const onScreen = (p) =>
    p && p.x >= 0 && p.y >= 0 && p.x <= camera.width && p.y <= camera.height;

  if (layers.planets) {
    ctx.fillStyle = 'rgba(255, 226, 168, 0.95)';
    ctx.font = `600 ${12 * dpr}px system-ui, sans-serif`;
    ctx.textAlign = 'left';
    for (const planet of round.planets) {
      const p = projectVector(altAzToVector(planet.alt, planet.az), camera);
      if (!onScreen(p)) continue;
      const at = besideMark(p, planet.name, 12 * dpr);
      if (at) ctx.fillText(planet.name, at.x, at.y);
    }
  }

  if (layers.stars) {
    ctx.fillStyle = 'rgba(236, 244, 255, 0.92)';
    ctx.font = `${12 * dpr}px system-ui, sans-serif`;
    ctx.textAlign = 'left';
    for (const star of namedStars) {
      const p = projectVector(altAzToVector(star.alt, star.az), camera);
      if (!onScreen(p)) continue;
      const at = besideMark(p, star.name, 12 * dpr);
      if (at) ctx.fillText(star.name, at.x, at.y);
    }
  }

  if (layers.constellations) {
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(150, 190, 245, 0.72)';
    ctx.font = `${11 * dpr}px system-ui, sans-serif`;
    for (const figure of round.constellations) {
      const p = projectVector(altAzToVector(figure.label.alt, figure.label.az), camera);
      if (!p) continue;
      const name = figure.name || figure.id;
      const width = ctx.measureText(name).width;
      if (!reserve(p.x - width / 2, p.y - 11 * dpr, p.x + width / 2, p.y + 3 * dpr)) continue;
      ctx.fillText(name, p.x, p.y);
      // Only what was actually drawn is hoverable, or the lore card opens off a
      // name that is not on screen.
      if (labelHits) labelHits.push({ figure, x: p.x, y: p.y, width });
    }
  }

  ctx.restore();
}

/*
 * `view`/`onViewChange` make the camera controllable from outside, which the
 * tutorial uses to fly it between lessons; left out, the viewer owns its own
 * camera as the game does. `overlay` draws lesson graphics on top of the
 * finished sky, so the tutorial's guides live in the tutorial rather than here.
 *
 * `assisted` is the Easy tier: names, the compass and the hovercards are all on
 * from the first moment instead of waiting for the guess. Everything the reveal
 * turns on, it turns on early — so the two tiers share one code path and cannot
 * drift into showing different things.
 */
export default function SkyViewer({
  round,
  texture,
  moonTexture,
  initialAz = 180,
  revealed = false,
  assisted = false,
  view: controlledView,
  onViewChange,
  /*
   * Whether the camera answers to the player at all. False for every tutorial
   * lesson but the explorer, where the lesson drives the camera itself and view
   * changes are swallowed — the arrows sat there looking pressable and did
   * nothing, which is worse than not offering them.
   */
  canPan = true,
  overlay,
  showHint = true,
  guideMode = false
}) {
  /*
   * Which magnitude-to-pixels curve to draw with.
   *
   * Two things can name one: the player's setting, and the dev tuner behind
   * ?brightness. The tuner wins when it is on, because its whole job is to
   * override whatever is configured and flip between curves over one unchanging
   * sky; otherwise the setting decides. Before the settings menu existed this
   * was always the shipped default, and the tuner's own note about not letting a
   * tuning session become a permanent sky still holds — that is why it keeps its
   * own storage key and does not write to the player's.
   */
  const tuner = useBrightnessCurve();
  const settings = useSettings();
  const curve = useMemo(
    () => (tuner.enabled ? tuner.curve : makeCurve(settings.brightness)),
    [tuner.enabled, tuner.curve, settings.brightness]
  );

  const canvasRef = useRef(null);
  const milkyWayRef = useRef(null);
  const moonFaceRef = useRef(null);
  const spriteCache = useRef(new Map());
  const cameraKey = useRef('');
  const frameRef = useRef(0);
  const dragRef = useRef(null);
  /*
   * Every finger currently on the canvas, by pointer id. A mouse only ever puts
   * one thing down and the drag anchor was enough; a pinch needs both fingers
   * tracked at once, and needs to know when it drops back to one so the
   * remaining finger can take over the drag instead of the view jumping.
   */
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const screenStars = useRef([]);
  const screenLabels = useRef([]);
  const setViewRef = useRef(null);

  const [ownView, setOwnView] = useState({ az: initialAz, alt: START_PITCH_DEG, fov: DEFAULT_FOV });
  const view = controlledView || ownView;
  const setView = useCallback(
    (next) => {
      const resolve = (prev) => (typeof next === 'function' ? next(prev) : next);
      if (onViewChange) onViewChange(resolve(view));
      else setOwnView(resolve);
    },
    [onViewChange, view]
  );
  const [hover, setHover] = useState(null);
  const [layers, setLayers] = useState({ stars: true, constellations: true, planets: true });

  // Fixed for the round, so panning moves labels rather than reshuffling them.
  const namedStars = useMemo(() => chooseStarLabels(round.stars, MAX_STAR_LABELS), [round]);

  /*
   * What the constellation hovercard reports, worked out from the round's own
   * stars rather than from a table of blurbs: how many of its stars are up, and
   * which is the brightest. Stars carry their constellation's full name, which
   * is the same string the figures use, so the two join directly.
   */
  const constellationFacts = useMemo(() => {
    const facts = new Map();
    for (const star of round.stars) {
      if (!star.constellation) continue;
      const entry = facts.get(star.constellation) || { count: 0, brightest: null };
      entry.count += 1;
      if (!entry.brightest || star.mag < entry.brightest.mag) entry.brightest = star;
      facts.set(star.constellation, entry);
    }
    return facts;
  }, [round]);

  setViewRef.current = setView;

  useEffect(() => {
    if (!controlledView) setOwnView({ az: initialAz, alt: START_PITCH_DEG, fov: DEFAULT_FOV });
    setHover(null);
    cameraKey.current = '';
  }, [round, initialAz, controlledView]);

  useEffect(() => {
    if (moonTexture && !moonFaceRef.current) {
      moonFaceRef.current = makeMoonFace(moonTexture, MOON_RADIUS_PX);
    }
  }, [moonTexture]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !round) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    if (!cssWidth || !cssHeight) return;

    if (canvas.width !== Math.round(cssWidth * dpr) || canvas.height !== Math.round(cssHeight * dpr)) {
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      cameraKey.current = '';
    }

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    /*
     * `MAX_FOV` is the widest this projection should be asked for, and until now
     * it was only being enforced across the frame's width. On a portrait phone
     * the derived vertical field ran to about 141°, which is past the point a
     * rectilinear projection means anything: the scale factor off-axis goes as
     * 1/cos², so at 70° from centre the top and bottom of the frame were
     * stretched some eight times and nearly half the screen was ground.
     *
     * So the cap applies to both axes. Taking the narrower of the two fields is
     * what enforces it — a narrower horizontal field is a longer focal length,
     * which pulls the vertical one in with it. On any window wider than it is
     * tall, which is every desktop one, the second term is the larger and this
     * changes nothing.
     */
    const fovDeg = Math.min(view.fov, horizontalFovDeg(width, height, MAX_FOV));

    const camera = makeCamera({
      azDeg: view.az,
      altDeg: view.alt,
      width,
      height,
      fovDeg
    });

    const moonWashout = round.moon.alt > 0
      ? round.moon.illuminated * Math.min(1, round.moon.alt / 30)
      : 0;

    /*
     * Daylight. Always 0 in a game round, since rounds are only ever generated
     * in astronomical night — this is for the tutorial's explorer, where the
     * observer can be dragged into the lit half of the world.
     */
    const day = daylightFactor(round.sun.alt);
    const starlight = 1 - day;

    // A moonlit sky is not black — it is a dim blue. Painting that in is what
    // makes the washed-out stars read as moonlight rather than as a bug. Then
    // twilight lifts the whole thing toward daylight on top of that.
    const glow = Math.round(moonWashout * 14);
    const mix = (night, lit) => Math.round(night + (lit - night) * day);
    ctx.fillStyle = `rgb(${mix(4 + glow, 122)}, ${mix(6 + glow, 168)}, ${mix(13 + Math.round(glow * 1.5), 220)})`;
    ctx.fillRect(0, 0, width, height);

    if (texture) {
      const lowWidth = Math.max(2, Math.round(width / MILKY_WAY_DOWNSCALE));
      const lowHeight = Math.max(2, Math.round(height / MILKY_WAY_DOWNSCALE));

      if (!milkyWayRef.current || milkyWayRef.current.width !== lowWidth || milkyWayRef.current.height !== lowHeight) {
        const buffer = document.createElement('canvas');
        buffer.width = lowWidth;
        buffer.height = lowHeight;
        milkyWayRef.current = { canvas: buffer, ctx: buffer.getContext('2d'), width: lowWidth, height: lowHeight };
        cameraKey.current = '';
      }

      /*
       * The brightness preset is part of the key, not just the camera. The band
       * is painted into a buffer and only repainted when this changes, so
       * without it a settings change left the old gain on screen until you
       * happened to pan.
       */
      const key = `${view.az.toFixed(2)}_${view.alt.toFixed(2)}_${view.fov.toFixed(2)}_${curve.milkyWay}`;
      if (key !== cameraKey.current) {
        const jd = julianDate(round.date);
        drawMilkyWay(
          milkyWayRef.current,
          camera,
          texture,
          round.lat,
          lstDegrees(jd, round.lon),
          moonWashout,
          starlight,
          curve.milkyWay
        );
        cameraKey.current = key;
      }

      ctx.globalCompositeOperation = 'lighter';
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(milkyWayRef.current.canvas, 0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';
    }

    // Scintillation: real and strongest near the horizon, where the line of
    // sight cuts through the most air.
    const time = performance.now() / 1000;

    // Rebuilt each frame so the hover test uses exactly what is on screen now.
    const onScreen = [];

    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < round.stars.length; i += 1) {
      const star = round.stars[i];
      const projected = projectVector(altAzToVector(star.alt, star.az), camera);
      if (!projected) continue;
      if (projected.x < -20 || projected.y < -20 || projected.x > width + 20 || projected.y > height + 20) continue;
      if (star.mag < 4.2) onScreen.push({ star, x: projected.x, y: projected.y });

      const dim = EXTINCTION_LUT[Math.max(0, (Math.sin((star.alt * Math.PI) / 180) * 128) | 0)];
      if (dim <= 0.01) continue;

      const twinkle = 1 + (1 - dim) * 0.35 * Math.sin(time * 3.1 + i * 1.7);
      const brightness = curve.brightness(star.mag) * dim * twinkle;
      if (brightness <= 0.01) continue;

      /*
       * Moonlight raises the background of the sky rather than dimming the stars,
       * so it buries the faint ones and leaves the bright ones essentially
       * untouched — under a full moon you still see Vega, just not the Milky Way.
       * Scaling every star by the same factor instead would empty the sky out.
       */
      const alpha =
        curve.alpha(brightness) *
        curve.magFade(star.mag) *
        (1 - moonWashout * 0.55 * (1 - brightness)) *
        starlight;
      if (alpha <= 0.02) continue;

      const sprite = spriteFor(
        spriteCache.current,
        starColor(star.spectralClass),
        curve.radius(brightness) * dpr,
        curve.coreFloor
      );
      ctx.globalAlpha = Math.min(1, alpha);
      ctx.drawImage(sprite.canvas, projected.x - sprite.offset, projected.y - sprite.offset);
    }
    /*
     * Planets sit on the same brightness scale as the stars but are drawn as
     * steady discs: they are close enough to show a disc rather than a point, so
     * they do not scintillate. Picking them out by their steadiness is how you
     * tell a planet from a star by eye.
     */
    for (const planet of round.planets) {
      const projected = projectVector(altAzToVector(planet.alt, planet.az), camera);
      if (!projected) continue;

      const dim = EXTINCTION_LUT[Math.max(0, (Math.sin((planet.alt * Math.PI) / 180) * 128) | 0)];
      if (dim <= 0.01) continue;

      const brightness = curve.brightness(planet.mag) * dim;
      const alpha =
        curve.alpha(brightness) * (1 - moonWashout * 0.55 * (1 - brightness)) * starlight;
      if (alpha <= 0.02) continue;

      const sprite = spriteFor(
        spriteCache.current,
        PLANET_COLOR,
        curve.radius(brightness) * 1.15 * dpr,
        curve.coreFloor
      );
      ctx.globalAlpha = Math.min(1, alpha);
      ctx.drawImage(sprite.canvas, projected.x - sprite.offset, projected.y - sprite.offset);
      onScreen.push({ star: planetAsTarget(planet), x: projected.x, y: projected.y });
    }

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    screenStars.current = onScreen;

    if (round.moon.alt > -5) drawMoon(ctx, camera, round.moon, round.sun, moonFaceRef.current);
    /*
     * The tutorial draws constellation figures without the rest of the reveal.
     * Its lessons are about recognising shapes, and the pointers are meaningless
     * unless you can see the Plough they come off; but naming every bright star
     * and pinning a compass to the corner would clutter what each lesson is
     * trying to isolate.
     */
    const labelHits = [];
    const annotated = revealed || assisted;
    /*
     * Figures and names fade out with the stars they name. Left at full strength
     * they hang in a daylit sky labelling constellations nobody can see, which
     * looks like a bug rather than like daytime.
     */
    ctx.globalAlpha = starlight;
    if (guideMode) {
      drawAnnotations(ctx, camera, round, dpr, GUIDE_LAYERS, namedStars, null);
    } else if (annotated) {
      drawAnnotations(ctx, camera, round, dpr, layers, namedStars, labelHits);
    }
    ctx.globalAlpha = 1;
    screenLabels.current = labelHits;

    const horizon = projectVector(altAzToVector(0, view.az), camera);
    if (horizon) {
      const y = Math.max(-height, Math.min(height * 2, horizon.y));
      /*
       * Drawn heavier and named in the tutorial. Every lesson measures something
       * up from this line — a latitude, a pole, an altitude — so a newcomer has
       * to know it is the ground and not just where the stars happen to stop.
       */
      const band = guideMode ? 0.22 : 0.16;
      const haze = ctx.createLinearGradient(0, y - height * band, 0, y);
      haze.addColorStop(0, 'rgba(18,26,44,0)');
      haze.addColorStop(1, 'rgba(24,32,52,0.75)');
      ctx.fillStyle = haze;
      ctx.fillRect(0, y - height * band, width, height * band);

      ctx.fillStyle = `rgb(${mix(5, 74)}, ${mix(7, 82)}, ${mix(12, 88)})`;
      ctx.fillRect(0, y, width, height - y);
      ctx.strokeStyle = guideMode ? 'rgba(150,185,235,0.7)' : 'rgba(120,150,200,0.35)';
      ctx.lineWidth = (guideMode ? 2.5 : 1) * dpr;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();

      if (guideMode && y > 26 * dpr && y < height - 6 * dpr) {
        ctx.save();
        ctx.fillStyle = 'rgba(180,205,245,0.8)';
        ctx.font = `600 ${12 * dpr}px system-ui, sans-serif`;
        // Right-hand end, where nothing else lands: the lesson dots run along the
        // bottom centre and the guides hang off their stars near the middle.
        ctx.textAlign = 'right';
        ctx.letterSpacing = `${1.4 * dpr}px`;
        ctx.fillText('THE HORIZON', width - 18 * dpr, y - 9 * dpr);
        ctx.restore();
      }
    }

    // After the horizon, so the ticks sit on top of the ground rather than under it.
    if (annotated && !guideMode) drawCompass(ctx, camera, dpr);
    if (overlay) {
      // The tutorial's guides go with the sky too: a pole marker floating over a
      // blue afternoon is pointing at nothing anyone could find.
      ctx.globalAlpha = starlight;
      overlay(ctx, camera, dpr);
      ctx.globalAlpha = 1;
    }
  }, [round, texture, view, revealed, assisted, layers, namedStars, overlay, guideMode, curve]);

  useEffect(() => {
    let running = true;
    const loop = () => {
      if (!running) return;
      draw();
      frameRef.current = requestAnimationFrame(loop);
    };
    frameRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(frameRef.current);
    };
  }, [draw]);

  /*
   * Which side of the star its card opens on.
   *
   * The card is up to 250px wide and the stage clips what runs past its edge, so
   * a star in the right-hand quarter of a phone-width sky produced a card with
   * its facts sliced off. Flipping it to the left of the star costs nothing and
   * is what a tooltip near an edge is supposed to do.
   */
  const cardSide = (x, canvasWidth) =>
    x + CARD_REACH > canvasWidth && x > CARD_REACH ? 'left' : 'right';

  // The distance between the two fingers, which is the whole of a pinch.
  const pinchGap = () => {
    const [a, b] = [...pointersRef.current.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    setHover(null);

    /*
     * A second finger turns the gesture into a zoom and ends the pan. The field
     * is scaled from the gap the pinch *started* at rather than stepped frame by
     * frame, so spreading and closing again lands back where it began instead of
     * drifting — the same reason a map does it that way.
     */
    if (pointersRef.current.size === 2) {
      dragRef.current = null;
      pinchRef.current = { startGap: pinchGap(), startFov: view.fov };
    } else if (pointersRef.current.size === 1) {
      dragRef.current = { x: event.clientX, y: event.clientY };
    }
  };

  const onPointerMove = (event) => {
    if (pointersRef.current.has(event.pointerId)) {
      pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }

    if (pinchRef.current && pointersRef.current.size >= 2) {
      const gap = pinchGap();
      if (gap > 0) {
        const { startGap, startFov } = pinchRef.current;
        // Fingers apart is a narrower field, which is zooming in.
        const fov = (startFov * startGap) / gap;
        setView((prev) => ({ ...prev, fov: Math.max(MIN_FOV, Math.min(MAX_FOV, fov)) }));
      }
      return;
    }

    if (!dragRef.current) {
      // In Hard, star details are part of the reveal: before the guess they
      // would name constellations and hand over the hemisphere for free. In
      // Easy that is exactly the point, so they are on from the start.
      if ((!revealed && !assisted) || guideMode) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = canvas.width / rect.width;
      const px = (event.clientX - rect.left) * dpr;
      const py = (event.clientY - rect.top) * dpr;

      // Generous hit radius. A star is a couple of pixels across, and asking
      // anyone to land a cursor on that exactly would make the tooltip useless.
      const reach = 14 * dpr;
      let best = null;
      let bestDistance = reach * reach;
      for (const candidate of screenStars.current) {
        const d = (candidate.x - px) ** 2 + (candidate.y - py) ** 2;
        if (d < bestDistance) {
          bestDistance = d;
          best = candidate;
        }
      }

      if (best) {
        setHover({
          kind: 'star',
          star: best.star,
          x: best.x / dpr,
          y: best.y / dpr,
          side: cardSide(best.x / dpr, rect.width)
        });
        return;
      }

      // Then the constellation names. Hit against the drawn text box, since the
      // name is what there is to point at.
      /*
       * The drawn text box, generously padded. The label is centred on its
       * anchor with the baseline at that y, so the glyphs sit just above it.
       */
      const padX = 14 * dpr;
      const padY = 14 * dpr;
      const found = screenLabels.current.find(
        (hit) =>
          px > hit.x - hit.width / 2 - padX &&
          px < hit.x + hit.width / 2 + padX &&
          py > hit.y - 12 * dpr - padY &&
          py < hit.y + 5 * dpr + padY
      );

      setHover(
        found
          ? {
              kind: 'constellation',
              figure: found.figure,
              x: found.x / dpr,
              y: found.y / dpr,
              side: cardSide(found.x / dpr, rect.width)
            }
          : null
      );
      return;
    }

    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    dragRef.current = { x: event.clientX, y: event.clientY };

    /*
     * Degrees per pixel has to be read off the field actually drawn, not the one
     * asked for. Where the cap in `draw` bites, `view.fov` overstates the frame
     * by half again, and using it here made a drag on a phone outrun the sky
     * under the finger.
     */
    const canvas = canvasRef.current;
    const shownFov = canvas
      ? Math.min(view.fov, horizontalFovDeg(canvas.width, canvas.height, MAX_FOV))
      : view.fov;
    const degreesPerPixel = shownFov / (canvas ? canvas.clientWidth : 800);

    /*
     * By default dragging moves the sky with the cursor, the way dragging a map
     * does. Inverted swings the camera instead, which is what a flight-sim
     * free-look does and what a good half of people expect from a 3D view —
     * hence the setting rather than a decision.
     */
    const sense = settings.invertDrag === 'inverted' ? -1 : 1;

    setView((prev) => ({
      ...prev,
      az: (prev.az - sense * dx * degreesPerPixel + 360) % 360,
      alt: Math.max(
        MIN_PITCH_DEG,
        Math.min(MAX_PITCH_DEG, prev.alt + sense * dy * degreesPerPixel)
      )
    }));
  };

  const endDrag = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointersRef.current.delete(event.pointerId);

    if (pointersRef.current.size < 2) pinchRef.current = null;

    /*
     * Lifting one finger of a pinch hands the pan back to the other one, anchored
     * where it currently is. Without the re-anchor the next move would be
     * measured against wherever the *first* finger went down, and the sky would
     * jump by the width of the gesture at the moment of release.
     */
    if (pointersRef.current.size === 1) {
      const [only] = pointersRef.current.values();
      dragRef.current = { x: only.x, y: only.y };
    } else {
      dragRef.current = null;
    }
  };

  /*
   * Zoom is bound natively rather than through React's onWheel, and the reason is
   * specific: React registers wheel as a passive listener, so preventDefault
   * inside an onWheel prop is ignored. On a Mac trackpad a pinch arrives as a
   * wheel event with ctrlKey set, and without preventDefault the browser zooms
   * the whole page on top of whatever we do to the sky.
   *
   * Only the canvas is claimed. Pinching anywhere else on the page still zooms
   * the page, which is the accessible default and not ours to take away.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const onWheel = (event) => {
      event.preventDefault();

      // A pinch reports small deltas and a scroll wheel large ones, so they need
      // different gains to feel like the same gesture.
      const gain = event.ctrlKey ? 0.55 : 0.14;
      const step = Math.max(-14, Math.min(14, event.deltaY * gain));

      setViewRef.current((prev) => ({
        ...prev,
        fov: Math.max(MIN_FOV, Math.min(MAX_FOV, prev.fov + step))
      }));
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  const nudge = (dAz, dAlt) => {
    setView((prev) => ({
      ...prev,
      az: (prev.az + dAz + 360) % 360,
      alt: Math.max(MIN_PITCH_DEG, Math.min(MAX_PITCH_DEG, prev.alt + dAlt))
    }));
  };

  return (
    <div className="sg-sky">
      <canvas
        ref={canvasRef}
        className={`sg-sky-canvas${canPan ? '' : ' is-fixed'}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={() => setHover(null)}
        role="img"
        aria-label="The night sky at the hidden location. Drag to look around."
      />

      {tuner.enabled && <BrightnessTuner presetKey={tuner.presetKey} onChange={tuner.setPreset} />}

      {hover && hover.kind === 'star' && (
        <StarCard star={hover.star} x={hover.x} y={hover.y} side={hover.side} />
      )}
      {hover && hover.kind === 'constellation' && (
        <ConstellationCard
          figure={hover.figure}
          facts={constellationFacts.get(hover.figure.name)}
          x={hover.x}
          y={hover.y}
          side={hover.side}
        />
      )}

      {(revealed || assisted) && !guideMode && (
        <div className="sg-layers" role="group" aria-label="Sky labels">
          {[
            ['constellations', 'Constellations'],
            ['stars', 'Stars'],
            ['planets', 'Planets']
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={layers[key] ? 'is-on' : ''}
              aria-pressed={layers[key]}
              onClick={() => setLayers((prev) => ({ ...prev, [key]: !prev[key] }))}
            >
              <span className="sg-layer-box" aria-hidden="true">{layers[key] ? '✓' : ''}</span>
              {label}
            </button>
          ))}
        </div>
      )}

      {(revealed || assisted) && !guideMode && <CompassRose azimuth={view.az} />}

      {/*
        Both gestures are named, and the stylesheet shows the one this device
        has — `(pointer: coarse)` picks the pinch. Doing it in CSS rather than
        with a media-query hook keeps the hint correct on a laptop with a
        touchscreen, where the honest answer is that either will work and the
        one the reader just used is the one they will see.
      */}
      {showHint && !revealed && (
        <div className="sg-sky-hint">
          drag to look around &middot; <span className="sg-hint-fine">scroll</span>
          <span className="sg-hint-coarse">pinch</span> to zoom
        </div>
      )}
      {canPan && (
        <div className="sg-sky-keys">
          <button type="button" onClick={() => nudge(-15, 0)} aria-label="Look left">&#8592;</button>
          <button type="button" onClick={() => nudge(0, 8)} aria-label="Look up">&#8593;</button>
          <button type="button" onClick={() => nudge(0, -8)} aria-label="Look down">&#8595;</button>
          <button type="button" onClick={() => nudge(15, 0)} aria-label="Look right">&#8594;</button>
        </div>
      )}
    </div>
  );
}

const SPECTRAL_NOTES = {
  O: 'blue, ferociously hot',
  B: 'blue-white',
  A: 'white',
  F: 'yellow-white',
  G: 'yellow, like the Sun',
  K: 'orange',
  M: 'red, cool'
};

// Exported so the landing page's backdrop can show the same card. One card
// component, so a star reads identically wherever you point at it.
export function StarCard({ star, x, y, side = 'right' }) {
  const rows = [
    ['Constellation', star.constellation || null],
    ['Magnitude', star.mag.toFixed(2)],
    [
      'Distance',
      star.isPlanet
        ? `${star.distanceAu.toFixed(2)} AU from Earth`
        : star.distance
          ? `${star.distance.toLocaleString()} light years`
          : null
    ],
    ['Type', SPECTRAL_NOTES[star.spectralClass] ? `${star.spectralClass}, ${SPECTRAL_NOTES[star.spectralClass]}` : null],
    ['Height', `${star.alt.toFixed(1)}° above the horizon`],
    ['Declination', `${star.dec.toFixed(1)}°`]
  ].filter(([, value]) => value);

  return (
    <div
      className={`sg-star-card${side === 'left' ? ' is-left' : ''}`}
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      <p className="sg-star-name">{star.name || star.bayer || 'unnamed star'}</p>
      {star.isPlanet && <p className="sg-star-bayer">planet</p>}
      {!star.isPlanet && star.name && star.bayer && <p className="sg-star-bayer">{star.bayer}</p>}
      <dl>
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/*
 * A compass rose fixed to the world, not to the screen: the ring turns as you
 * look around, and the fixed needle at the top is the direction you are facing.
 *
 * Screen up is the view azimuth, so a bearing D sits (D - view) clockwise from
 * up, which in SVG coordinates (y downward) is (sin, -cos) of that angle.
 */
function CompassRose({ azimuth }) {
  const radius = 34;

  return (
    <div className="sg-compass" title={`Facing ${bearingName(azimuth)}`}>
      <svg viewBox="-50 -50 100 100" aria-hidden="true">
        <circle className="sg-compass-ring" r={radius + 8} />
        {CARDINALS.map(([label, bearing]) => {
          const angle = ((bearing - azimuth) * Math.PI) / 180;
          const major = label.length === 1;
          const r = major ? radius : radius + 1;
          return (
            <text
              key={label}
              className={major ? 'sg-compass-major' : 'sg-compass-minor'}
              x={r * Math.sin(angle)}
              y={-r * Math.cos(angle)}
            >
              {label}
            </text>
          );
        })}
        <path className="sg-compass-needle" d="M0,-46 L6,-36 L-6,-36 Z" />
      </svg>
      <span className="sg-compass-label">facing {bearingName(azimuth)}</span>
    </div>
  );
}

/*
 * Where the constellation card differs from the star card: a constellation is
 * less a fact about the sky than a fact about the people who looked at it, and
 * counting how many of its stars happen to be above the horizon says nothing
 * about it. So the card leads with where the figure came from, and keeps only
 * the brightest star, which is the part you can actually go and look at.
 */
function ConstellationCard({ figure, facts, x, y, side = 'right' }) {
  const lore = loreFor(figure.id);
  const brightest = facts && facts.brightest;

  return (
    <div
      className={`sg-star-card sg-lore-card${side === 'left' ? ' is-left' : ''}`}
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      <p className="sg-star-name">{figure.name || figure.id}</p>
      <p className="sg-star-bayer">constellation &middot; {figure.id}</p>
      {lore && <p className="sg-lore">{lore}</p>}
      {brightest && (
        <p className="sg-lore-star">
          <span className="sg-muted">Brightest star:</span>{' '}
          {brightest.name || brightest.bayer || 'unnamed'}
          <span className="sg-muted"> (mag {brightest.mag.toFixed(2)})</span>
        </p>
      )}
    </div>
  );
}
